import { useState, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import RawMaterialService from "../../services/RawMaterials/RawMaterialService";
import { Tag } from "primereact/tag";
import { generateOutwardChallanPDF } from "../Outward-Challan/OutwardChallan-PDF";
import RMEWayBill from "./RM-EWayBill";
import posService from "../../services/pos/pos.service";
import styles from "./RMOutwardChallan-Datatable.module.css";

interface RMOutwardChallan {
  movementType: string;
  outwardId: number;
  rawMaterialName: string;
  quantity: number;
  createdDate: string;
  sourceType: string;
  sourceValue?: string;
  documentDetails?: string;
  notes?: string;
}

interface Props {
  data: RMOutwardChallan[];
  refreshData: () => void;
  challanCount: number;
}

export const RMOutwardChallan = ({ data, refreshData, challanCount }: Props) => {
  const tableData = data || [];
  const toast = useRef<Toast>(null);
  const [selectedRows, setSelectedRows] = useState<RMOutwardChallan[]>([]);
  const [ewayOrder, setEwayOrder] = useState<any>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showEwayDialog, setShowEwayDialog] = useState(false);

  const sortedData = [...tableData].sort(
    (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
  );

  const typeBody = (rowData: RMOutwardChallan) => {
    let severity: "info" | "success" | "warning" | undefined;
    switch (rowData.movementType) {
      case "Inward":
        severity = "success";
        break;
      case "Outward":
        severity = "info";
        break;
      case "Adjust":
        severity = "warning";
        break;
      default:
        severity = undefined;
    }
    return <Tag value={rowData.movementType} severity={severity} />;
  };

  const dateBody = (rowData: RMOutwardChallan) => {
    const dt = new Date(rowData.createdDate);
    return dt.toLocaleDateString("en-GB");
  };

  const handleGenerate = async () => {
    const safeSelectedRows = selectedRows || [];
    if (safeSelectedRows.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Please select at least one record to generate challan.",
      });
      return;
    }

    setIsGenerating(true);
    try {
      // 1️⃣ Group rows by sourceValue (non-null)
      const sourceMap = new Map<string, RMOutwardChallan[]>();
      const noSourceRows: RMOutwardChallan[] = [];

      for (const row of safeSelectedRows) {
        if (row.sourceValue) {
          if (!sourceMap.has(row.sourceValue)) {
            sourceMap.set(row.sourceValue, []);
          }
          sourceMap.get(row.sourceValue)!.push(row);
        } else {
          noSourceRows.push(row);
        }
      }

      // 2️⃣ Generate challan for each sourceValue group
      for (const [, rows] of sourceMap) {
        const payload = {
          outwardIds: rows.map(r => r.outwardId),
        };

        const response = await RawMaterialService.generateRMOutwardChallan(payload);

        generateOutwardChallanPDF({
          categorySummaries: response?.categorySummaries || [],
          lineItems: response?.lineItems || [],
          challanNo: response?.challanNo?.toString() || "",
          fromAddress: response?.fromAddress || "",
          toAddress: response?.toAddress || "",
          generatedBy: response?.generatedBy || "",
        });

        setEwayOrder(response);
      }

      // 3️⃣ Generate challan SEPARATELY for each null source row
      if (noSourceRows.length > 0) {
        const payload = {
          outwardIds: noSourceRows.map(r => r.outwardId),
        };
        const response = await RawMaterialService.generateRMOutwardChallan(payload);

        generateOutwardChallanPDF({
          categorySummaries: response?.categorySummaries || [],
          lineItems: response?.lineItems || [],
          challanNo: response?.challanNo?.toString() || "",
          fromAddress: response?.fromAddress || "",
          toAddress: response?.toAddress || "",
          generatedBy: response?.generatedBy || "",
        });

        setEwayOrder(response);
      }

      setSelectedRows([]);
      refreshData();

      toast.current?.show({
        severity: "success",
        summary: "Challan Generated",
        detail: "Outward challan(s) created successfully.",
      });
    } catch (error) {
      console.error("Error generating challan:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to generate challan.",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const fetchTodaysChallan = async () => {
    try {
      const today = new Date();
      const challans = await posService.viewRMOutwardChallan(today, today);

      if (!challans || challans.length === 0) {
        toast.current?.show({
          severity: "info",
          summary: "No Data",
          detail: "No challan found for today.",
        });
        return null;
      }

      // Filter out challans where hasEwayBill is true
      const eligibleChallans = challans.filter((c: any) => !c.hasEwayBill);

      if (eligibleChallans.length === 0) {
        toast.current?.show({
          severity: "info",
          summary: "No E-Way Bill Eligible Challans",
          detail: "All today's challans already have an E-Way Bill.",
        });
        return null;
      }
      return eligibleChallans[0];
    } catch (error) {
      console.error("Error fetching today's challan:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch challan details.",
      });
      return null;
    }
  };

  const handleGenerateEway = async () => {
    const challan = await fetchTodaysChallan();
    if (!challan) return;
    setEwayOrder(challan);
    setShowEwayDialog(true);
  };

  return (
    <div className={styles["table-card-wrapper"]}>
      <Toast ref={toast} />

      {/* Table Header */}
      <div className={styles["table-header"]}>
        <div className={styles["table-header-left"]}>
          <div className={styles["table-icon"]}>
            <i className="pi pi-inbox" />
          </div>
          <div className={styles["table-title"]}>Raw Material Outward Challan</div>
        </div>
        <div className={styles["table-actions"]}>
          <div className={styles["table-action-button"]}>
            <Button
              label={isGenerating ? "Generating..." : "Generate Outward Challan"}
              icon="pi pi-file"
              disabled={isGenerating || selectedRows.length === 0}
              loading={isGenerating}
              onClick={handleGenerate}
              severity="success"
            />
          </div>
          <div className={`${styles["table-action-button"]} ${styles["table-action-button-info"]}`}>
            <Button
              label={`Generate E-WayBill${challanCount ? ` (${challanCount})` : ""}`}
              icon="pi pi-truck"
              severity="info"
              onClick={handleGenerateEway}
            />
          </div>
        </div>
      </div>

      {/* DataTable */}
      <DataTable
        value={sortedData}
        selection={selectedRows}
        onSelectionChange={(e) => setSelectedRows(e.value || [])}
        dataKey="outwardId"
        selectionMode="checkbox"
        paginator
        rows={10}
        rowsPerPageOptions={[10, 25, 50, 100]}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        stripedRows
        tableStyle={{ minWidth: "100%" }}
      >
        <Column selectionMode="multiple" style={{ width: "3rem" }} />
        <Column header="Type" body={typeBody} style={{ width: "10%" }} />
        <Column header="Date" body={dateBody} style={{ width: "12%" }} />
        <Column field="rawMaterialName" header="Raw Material" style={{ width: "18%" }} />
        <Column field="quantity" header="Qty" style={{ width: "8%" }} />
        <Column field="sourceType" header="Source Type" style={{ width: "12%" }} />
        <Column field="sourceValue" header="Source" style={{ width: "12%" }} />
        <Column field="documentDetails" header="Document Details" style={{ width: "14%" }} />
        <Column field="notes" header="Notes" style={{ width: "15%" }} />
      </DataTable>

      <Dialog
        header="Generate E-Way Bill"
        visible={showEwayDialog}
        style={{ width: "50vw" }}
        modal
        onHide={() => setShowEwayDialog(false)}
      >
        {ewayOrder && (
          <RMEWayBill
            order={ewayOrder}
            onCancel={() => setShowEwayDialog(false)}
            onSuccess={() => {
              setShowEwayDialog(false);
              refreshData();
              toast.current?.show({
                severity: "success",
                summary: "Success",
                detail: "E-Way Bill generated successfully.",
              });
            }}
          />
        )}
      </Dialog>
    </div>
  );
};